import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import { ProgrammeIcon } from '../components/programmes/ProgrammeUI';
import { getProgramme } from '../data/programmeJourneys';
import { PROGRAMME_SYSTEM_MESSAGES } from '../data/programmeMessages';
import {
  completeProgrammePasswordReset,
  getProgrammeApplication,
  requestProgrammeAccess,
} from '../services/programmeJourneyService';
import '../styles/pages/ProgrammeJourneys.css';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const allowedProfileImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
const maxProfileImageSize = 4 * 1024 * 1024;

const ProgrammeAccessPage = ({ kind, mode = 'setup' }) => {
  const programme = getProgramme(kind);
  const application = useMemo(
    () => getProgrammeApplication(programme.key),
    [programme.key]
  );
  const [searchParams] = useSearchParams();
  const isReset = mode === 'reset';
  const resetToken = searchParams.get('token') || '';
  const isResetCompletion = isReset && Boolean(resetToken);
  const [values, setValues] = useState({
    email: application?.data?.email || '',
    reference: application?.id || '',
    password: '',
    confirmPassword: '',
    profileImage: null,
  });
  const [error, setError] = useState('');
  const [complete, setComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const profilePreview = useMemo(
    () =>
      values.profileImage && typeof URL !== 'undefined'
        ? URL.createObjectURL(values.profileImage)
        : '',
    [values.profileImage]
  );

  useEffect(
    () => () => {
      if (profilePreview && typeof URL !== 'undefined') {
        URL.revokeObjectURL(profilePreview);
      }
    },
    [profilePreview]
  );

  const updateValue = (name, value) => {
    setValues((current) => ({ ...current, [name]: value }));
    setError('');
  };

  const updateProfileImage = (file) => {
    if (!file) {
      updateValue('profileImage', null);
      return;
    }
    if (!allowedProfileImageTypes.includes(file.type)) {
      updateValue('profileImage', null);
      setError('Choose a JPG, PNG or WebP profile picture.');
      return;
    }
    if (file.size > maxProfileImageSize) {
      updateValue('profileImage', null);
      setError('Choose a profile picture smaller than 4 MB.');
      return;
    }
    updateValue('profileImage', file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isResetCompletion && !emailPattern.test(values.email.trim())) {
      setError('Enter a valid email address.');
      return;
    }

    if (!isReset || isResetCompletion) {
      if (!isReset && !values.profileImage) {
        setError('Add a profile picture to create portal access.');
        return;
      }
      if (!values.reference.trim()) {
        if (!isResetCompletion) {
          setError('Enter your application reference.');
          return;
        }
      }
      if (values.password.length < 8) {
        setError('Create a password with at least 8 characters.');
        return;
      }
      if (values.password !== values.confirmPassword) {
        setError('The passwords do not match.');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (isResetCompletion) {
        await completeProgrammePasswordReset(
          programme.key,
          resetToken,
          values.password
        );
      } else {
        await requestProgrammeAccess(
          programme.key,
          values.email.trim(),
          isReset ? 'password-reset' : 'setup',
          isReset
            ? {}
            : {
                reference: values.reference.trim(),
                password: values.password,
                profileImage: values.profileImage,
              }
        );
      }
      setComplete(true);
    } catch (requestError) {
      setError(PROGRAMME_SYSTEM_MESSAGES.accessFailure);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <SEO
        description={`${isResetCompletion ? 'Reset your password for' : isReset ? 'Recover' : 'Set up'} access to the ${programme.title} portal.`}
        title={`${isResetCompletion ? 'Reset password' : isReset ? 'Recover access' : 'Set up access'} | ${programme.title}`}
      />

      <main
        className={`pj-login-page pj-theme--${programme.accent}`}
        data-programme={programme.key}
      >
        <div className="pj-login-shell">
          <section className="pj-login-hero" aria-labelledby="access-title">
            <h1 id="access-title">
              {isResetCompletion
                ? 'Reset password'
                : isReset
                  ? 'Recover access'
                  : 'Set up access'}
            </h1>
            <p>
              {isResetCompletion
                ? `Choose a new password for your ${programme.portalName}.`
                : isReset
                ? `Request password recovery for your ${programme.portalName}.`
                : `Create the access details you will use for your ${programme.portalName}.`}
            </p>
          </section>

          <div className="pj-access-layout">
            {complete ? (
              <section
                className="pj-completion pj-completion--compact"
                aria-labelledby="access-complete-title"
              >
                <span className="pj-completion__icon" aria-hidden="true">
                  <ProgrammeIcon name="check" size={30} />
                </span>
                <h2 id="access-complete-title">
                  {isResetCompletion
                    ? 'Password updated.'
                    : isReset
                      ? 'Recovery requested.'
                      : 'Access setup complete.'}
                </h2>
                <p>
                  {isResetCompletion
                    ? 'Your new password is ready. You can now sign in.'
                    : isReset
                    ? 'If the email is connected to an account, recovery instructions will be sent.'
                    : 'You can now continue to the participant login.'}
                </p>
                <Link
                  className="pj-action pj-action--primary"
                  to={`/${programme.slug}/login`}
                >
                  Return to sign in
                  <ProgrammeIcon name="arrow" size={18} />
                </Link>
              </section>
            ) : (
              <section className="pj-login-panel pj-access-panel">
                <div className="pj-login-panel__heading">
                  <h2>
                    {isResetCompletion
                      ? 'Create a new password'
                      : isReset
                        ? 'Password recovery'
                        : 'Create portal access'}
                  </h2>
                  <p>
                    {isResetCompletion
                      ? 'Use at least 8 characters.'
                      : isReset
                      ? 'Use the email connected to your application.'
                      : 'Use the application reference from your confirmation.'}
                  </p>
                </div>

                <form noValidate onSubmit={handleSubmit}>
                  {!isReset ? (
                    <div className="pj-field pj-field--wide pj-profile-field">
                      <span
                        className="pj-profile-field__label"
                        id={`${programme.key}-profile-image-label`}
                      >
                        Profile picture <span aria-hidden="true">*</span>
                      </span>
                      <div className="pj-profile-upload">
                        <span
                          className={`pj-profile-upload__preview ${
                            profilePreview ? 'has-image' : ''
                          }`}
                          aria-hidden="true"
                        >
                          {profilePreview ? (
                            <img alt="" src={profilePreview} />
                          ) : (
                            <ProgrammeIcon name="user" size={26} />
                          )}
                        </span>
                        <div className="pj-profile-upload__content">
                          <strong>
                            {values.profileImage
                              ? values.profileImage.name
                              : 'Add a clear head-and-shoulders photo'}
                          </strong>
                          <span id={`${programme.key}-profile-image-help`}>
                            JPG, PNG or WebP. Maximum file size 4 MB.
                          </span>
                          <label
                            className="pj-profile-upload__action"
                            htmlFor={`${programme.key}-profile-image`}
                          >
                            {values.profileImage
                              ? 'Change picture'
                              : 'Choose picture'}
                          </label>
                          <input
                            accept="image/jpeg,image/png,image/webp"
                            aria-describedby={`${programme.key}-profile-image-help`}
                            aria-labelledby={`${programme.key}-profile-image-label`}
                            aria-required="true"
                            id={`${programme.key}-profile-image`}
                            name="profile_image"
                            onChange={(event) =>
                              updateProfileImage(event.target.files?.[0])
                            }
                            required
                            type="file"
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {!isReset ? (
                    <div className="pj-field pj-field--wide">
                      <label htmlFor={`${programme.key}-access-reference`}>
                        Application reference
                      </label>
                      <input
                        id={`${programme.key}-access-reference`}
                        onChange={(event) =>
                          updateValue('reference', event.target.value)
                        }
                        placeholder="Application reference"
                        type="text"
                        value={values.reference}
                      />
                    </div>
                  ) : null}

                  {!isResetCompletion ? (
                    <div className="pj-field pj-field--wide">
                      <label htmlFor={`${programme.key}-access-email`}>
                        Email address
                      </label>
                      <input
                        autoComplete="email"
                        id={`${programme.key}-access-email`}
                        onChange={(event) =>
                          updateValue('email', event.target.value)
                        }
                        placeholder="Email address"
                        type="email"
                        value={values.email}
                      />
                    </div>
                  ) : null}

                  {!isReset || isResetCompletion ? (
                    <>
                      <div className="pj-field pj-field--wide">
                        <label htmlFor={`${programme.key}-access-password`}>
                          Create password
                        </label>
                        <input
                          autoComplete="new-password"
                          id={`${programme.key}-access-password`}
                          onChange={(event) =>
                            updateValue('password', event.target.value)
                          }
                          placeholder="Create password"
                          type="password"
                          value={values.password}
                        />
                      </div>
                      <div className="pj-field pj-field--wide">
                        <label
                          htmlFor={`${programme.key}-access-confirm-password`}
                        >
                          Confirm password
                        </label>
                        <input
                          autoComplete="new-password"
                          id={`${programme.key}-access-confirm-password`}
                          onChange={(event) =>
                            updateValue('confirmPassword', event.target.value)
                          }
                          placeholder="Confirm password"
                          type="password"
                          value={values.confirmPassword}
                        />
                      </div>
                    </>
                  ) : null}

                  {error ? (
                    <p className="pj-login-error" role="alert">
                      {error}
                    </p>
                  ) : null}

                  <div className="pj-access-actions">
                    <button
                      className="pj-button pj-button--primary"
                      disabled={isSubmitting}
                      type="submit"
                    >
                      {isSubmitting
                        ? 'Sending…'
                        : isResetCompletion
                          ? 'Update password'
                          : isReset
                          ? 'Request recovery'
                          : 'Create access'}
                      {!isSubmitting ? (
                        <ProgrammeIcon name="arrow" size={18} />
                      ) : null}
                    </button>

                    <Link
                      className="pj-inline-link"
                      to={`/${programme.slug}/login`}
                    >
                      <ProgrammeIcon name="back" size={17} />
                      Return to sign in
                    </Link>
                  </div>
                </form>
              </section>
            )}
          </div>
        </div>
      </main>
    </PageWrapper>
  );
};

export default ProgrammeAccessPage;
